import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  Stack,
  Typography,
  OutlinedInput,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Menu,
  Modal,
} from "@mui/material";
import LayerdIcon from "../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../icons/uiLucideIcons";
import colors from "../../../../../utils/colors";
import V2PageHeader from "../../../../common/V2PageHeader";
import { tableHeadSx, tableBodySx } from "../../detection/WAFEvent";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { formatDate } from "../../../../../utils/format";
import { getRuleKeyTitle } from "../../../../../utils/constants";
import { getRuleActionString } from "../../../application/waf/rule/custom/component";
import { CrsSecRuleId } from "../../../../../utils/constants";
import { generateMenuSx } from "../../../../../layouts/_new/Normal";
import ConfigCustomRuleSettingsModal from "./components/modal/ConfigCustomRuleSettings";

import useAuth from "../../../../../hooks/useAuth";
import useWAFConfig from "../../../../../hooks/user/useWAFConfig";
import usePermissions from "../../../../../hooks/usePermissions";
import { CancelButton, PrimaryButton, SaveButton } from "../../../../common/Button";

const NewServicesWebApplicationCustomRules = () => {
  const { isAuthenticated } = useAuth();
  const { wafConfig, customRules, getCustomRules, errMsg, setErr, deleteCustomRule } = useWAFConfig();
  const { showError } = useNotification();
  const { hasRolePermission, PERMISSIONS } = usePermissions();

  const canRoleEditRules = hasRolePermission(PERMISSIONS.RULES_WRITE);
  const canRoleDeleteRules = hasRolePermission(PERMISSIONS.RULES_DELETE);

  useEffect(() => {
    if (isAuthenticated) {
      getCustomRules();
    }
  }, [isAuthenticated, getCustomRules]);

  useEffect(() => {
    if (errMsg) {
      showError(errMsg);
      setErr(null);
    }
  }, [errMsg, showError, setErr]);

  const [pattern, setPattern] = useState("");
  const [actionMenu, setActionMenu] = useState({
    anchorEl: null, // the DOM element the menu anchors to
    customRuleId: null, // index of subrow (or null if main row)
  });
  // open handler (call from the ActionIcon onClick)
  const handleActionOpen = (event, customRuleId = null) => {
    event.stopPropagation(); // prevent row expand/click side effects
    setActionMenu({ anchorEl: event.currentTarget, customRuleId });
  };

  // close handler
  const handleActionClose = () => {
    setActionMenu({ anchorEl: null, customRuleId: null });
  };

  const isActionMenuOpen = Boolean(actionMenu.anchorEl);

  const [customRuleIdToDelete, setCustomRuleIdToDelete] = useState(null);
  const [openCustomRuleToDelete, setOpenCustomRuleToDelete] = useState(false);
  const handleCustomRuleToDeleteOpen = () => setOpenCustomRuleToDelete(true);
  const handleCustomRuleToDeleteClose = () => setOpenCustomRuleToDelete(false);

  const [customRuleToEdit, setCustomRuleToEdit] = useState(null);
  const [openCustomRule, setOpenCustomRule] = useState(false);
  const handleCustomRuleOpen = () => setOpenCustomRule(true);
  const handleCustomRuleClose = () => {
    setOpenCustomRule(false);
    setCustomRuleToEdit(null);
  };

  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <>
      <Helmet title="Custom Rules" />
      <Stack spacing={4} sx={{ p: 6 }}>
        <V2PageHeader
          title="Custom Rules"
          helpTooltip="Define custom ModSecurity-style rules to block, challenge, or log requests matching your expressions. Rules apply site-wide after the WAF engines evaluate traffic."
          sx={{ pb: 0, alignItems: "center" }}
          actions={(
            <Stack direction="row" spacing={1} alignItems="center">
              <OutlinedInput
                startAdornment={<LayerdIcon icon={UI_ICONS.search} size={16} color="neutral" />}
                placeholder="Search Rule ID"
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                sx={{
                  width: 240,
                  height: 36,
                  borderRadius: "8px",
                  fontSize: 14,
                  lineHeight: "21px",
                  backgroundColor: colors.neutral[100],
                  pl: 2,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.neutral[200],
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.neutral[400],
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.primary[300],
                  },
                  "& .MuiOutlinedInput-input": {
                    fontSize: 14,
                    lineHeight: "21px",
                    px: 2,
                    py: 0,
                    height: "100%",
                    boxSizing: "border-box",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: 14,
                    lineHeight: "21px",
                  },
                }}
              />
              <PrimaryButton
                startIcon={<LayerdIcon icon={UI_ICONS.plus} size="sm" color="inherit" />}
                onClick={() => {
                  setCustomRuleToEdit(null);
                  handleCustomRuleOpen();
                }}
                disabled={!canRoleEditRules}
              >
                Add custom rule
              </PrimaryButton>
            </Stack>
          )}
        />
        <Box sx={{ width: "100%", borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2 }}>
          <Table
            sx={{
              "& .MuiTableCell-head": { py: 3, px: 3 },
              "& .MuiTableCell-body": { py: 2, px: 3 },
              "& .MuiTableCell-root": { borderBottom: "none" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "10%" }}>
                  <Typography sx={tableHeadSx}>Rule ID</Typography>
                </TableCell>
                <TableCell sx={{ width: "40%" }}>
                  <Typography sx={tableHeadSx}>Description</Typography>
                </TableCell>
                <TableCell sx={{ width: "10%" }}>
                  <Typography sx={tableHeadSx}>Action</Typography>
                </TableCell>
                <TableCell sx={{ width: "15%" }}>
                  <Typography sx={tableHeadSx}>Timestamp</Typography>
                </TableCell>
                <TableCell sx={{ width: "15%" }}>
                  <Typography sx={tableHeadSx}>Type</Typography>
                </TableCell>
                <TableCell sx={{ width: "10%" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customRules
                ?.filter((rule) => rule?.custom_rule_id?.toString()?.includes(pattern))
                ?.map((row, index) => (
                  <TableRow key={`custom_rule_${index}`}>
                    <TableCell>
                      <Typography sx={tableBodySx}>{row?.custom_rule_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{row?.description}</Typography>
                      <Typography sx={{ ...tableBodySx, color: colors.neutral[600] }}>
                        {[...new Set(row.conditions?.reduce((allConditions, condition) => allConditions.concat(condition.key), []))]
                          .map((condition) => getRuleKeyTitle(condition))
                          .join(", ")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{getRuleActionString(row?.action)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{formatDate(row?.created_date) || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction={"row"} alignItems={"center"} spacing={2}>
                        {CrsSecRuleId.MIN_CUSTOM_GLOBAL <= row?.custom_rule_id ? (
                          <LayerdIcon icon={UI_ICONS.brand} size={20} color="primary" />
                        ) : (
                          <LayerdIcon icon={UI_ICONS.user} size={24} color="neutral" />
                        )}
                        <Typography sx={tableBodySx}>
                          {CrsSecRuleId.MIN_CUSTOM_GLOBAL <= row?.custom_rule_id ? "Layerd AI Managed" : "User Managed"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack alignItems={"flex-end"} sx={{ width: "100%" }}>
                        <Box onClick={(e) => handleActionOpen(e, row?.custom_rule_id)} sx={{ cursor: "pointer", width: 24, height: 24 }}>
                          <LayerdIcon icon={UI_ICONS.more} size={18} color="neutral" />
                        </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              {customRules?.filter((rule) => rule?.custom_rule_id?.toString()?.includes(pattern))?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography sx={tableBodySx} textAlign="center">
                      There are no custom rules
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <Typography sx={tableBodySx}>Last updated {formatDate(wafConfig?.updated_at)}</Typography>
      </Stack>
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={isActionMenuOpen}
        onClose={handleActionClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 200,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              if (!canRoleEditRules) return;
              const { customRuleId } = actionMenu;
              setCustomRuleToEdit(customRules?.find((item) => item?.custom_rule_id === customRuleId));
              handleCustomRuleOpen();
              handleActionClose();
            }}
            sx={{
              ...generateMenuSx(176, 37),
              opacity: canRoleEditRules ? 1 : 0.4,
              cursor: canRoleEditRules ? "pointer" : "not-allowed",
              pointerEvents: canRoleEditRules ? "auto" : "none",
            }}
          >
            <LayerdIcon icon={UI_ICONS.edit} size={18} color="neutral" />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>Edit</Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              if (!canRoleDeleteRules) return;
              const { customRuleId } = actionMenu;
              setCustomRuleIdToDelete(customRuleId);
              handleCustomRuleToDeleteOpen();
              handleActionClose();
            }}
            sx={{
              ...generateMenuSx(176, 37),
              opacity: canRoleDeleteRules ? 1 : 0.4,
              cursor: canRoleDeleteRules ? "pointer" : "not-allowed",
              pointerEvents: canRoleDeleteRules ? "auto" : "none",
            }}
          >
            <LayerdIcon icon={UI_ICONS.delete} size={18} color="neutral" />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Delete
            </Typography>
          </Stack>
        </Box>
      </Menu>
      <Modal open={openCustomRuleToDelete} onClose={handleCustomRuleToDeleteClose}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
          }}
        >
          <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete Custom Rule?
            </Typography>
            <Box onClick={handleCustomRuleToDeleteClose} sx={{ cursor: "pointer" }}>
              <LayerdIcon icon={UI_ICONS.close} size={20} color="neutral" />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            Are you sure you want to permanently delete this custom rule?
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={4}>
            <CancelButton onClick={handleCustomRuleToDeleteClose} outlined={true} />
            <SaveButton
              onClick={async () => {
                if (!canRoleDeleteRules) return;
                setIsDeleting(true);
                await deleteCustomRule(customRuleIdToDelete);
                setIsDeleting(false);
                handleCustomRuleToDeleteClose();
              }}
              disabled={isDeleting || !canRoleDeleteRules}
              label="Delete"
            />
          </Stack>
        </Stack>
      </Modal>
      <ConfigCustomRuleSettingsModal open={openCustomRule} handleClose={handleCustomRuleClose} customRuleToEdit={customRuleToEdit} />
    </>
  );
};
export default NewServicesWebApplicationCustomRules;
